/**
 * TrialOfferPopup — first-visit modal pitching the 7-day Pro trial.
 *
 * Triggers:
 *   - 4 seconds after the visitor lands (enough to read the page first)
 *   - Only if eligible (visitor or free user who hasn't used the trial)
 *   - Only once per user (localStorage gate so it never re-pops)
 *   - Suppressed on auth/pricing/payment routes
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Crown, X, Lightning, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { useTrialOffer } from '@/hooks/useTrialOffer';

const SEEN_KEY = 'pp_trial_popup_seen';
const HIDE_PATHS = new Set([
  '/pricing',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/magic',
  '/payment-success',
]);

export default function TrialOfferPopup() {
  const { loading: authLoading } = useAuth();
  const { pathname } = useLocation();
  const { eligible, terms, starting, startTrial } = useTrialOffer();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (HIDE_PATHS.has(pathname)) return;
    if (!eligible) return;
    let seen = false;
    try { seen = localStorage.getItem(SEEN_KEY) === '1'; } catch (_) {}
    if (seen) return;
    const t = setTimeout(() => {
      setOpen(true);
      try { window.posthog?.capture('trial_offer_viewed', { surface: 'popup' }); } catch (_) {}
    }, 4000);
    return () => clearTimeout(t);
  }, [authLoading, eligible, pathname]);

  const close = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch (_) {}
    setOpen(false);
  };

  if (!open || !eligible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={close}
      data-testid="trial-popup-overlay"
    >
      <div
        className="relative w-full max-w-md card-tactical rounded-md border-[#FF6B1A]/50 shadow-[0_0_80px_rgba(255,107,26,0.35)] p-7"
        onClick={(e) => e.stopPropagation()}
        data-testid="trial-popup"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close trial offer"
          data-testid="trial-popup-close"
          className="absolute top-3 right-3 p-1.5 hover:bg-white/5 rounded-full text-white/40 hover:text-white"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="inline-flex items-center gap-2 px-2.5 py-1 mb-4 rounded-full bg-[#FF6B1A]/15 border border-[#FF6B1A]/40">
          <Crown size={12} weight="fill" className="text-[#FF6B1A]" />
          <span className="text-[10px] font-heading font-black uppercase tracking-[0.22em] text-white">
            Exclusive Offer
          </span>
        </div>

        <h2 className="font-heading font-black text-3xl sm:text-4xl uppercase tracking-tight text-white leading-[0.95] mb-2">
          7 DAYS<br />
          <span className="text-[#FF6B1A]">FREE.</span>
        </h2>
        <p className="text-sm text-white/60 mb-5">
          Try Parlay Posse Pro for 7 days — full AI engine, unlimited parlays, all 8 sports, ad-free. Cancel anytime.
        </p>

        <ul className="space-y-2 mb-6">
          {[
            'Unlimited AI-generated parlays',
            'Smart Signal alerts across all 8 sports',
            'Live parlay auto-scoring + push notifications',
            'Ad-free experience',
          ].map((b) => (
            <li key={b} className="flex items-start gap-2 text-xs sm:text-sm text-white/80">
              <CheckCircle size={16} weight="fill" className="shrink-0 text-[#34C759] mt-0.5" />
              {b}
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={startTrial}
          disabled={starting}
          data-testid="trial-popup-cta"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#FF6B1A] hover:bg-[#FF853E] disabled:opacity-60 text-black font-heading font-black text-base uppercase tracking-wide rounded-md transition-colors"
        >
          <Lightning size={20} weight="fill" />
          {starting ? 'STARTING…' : 'START 7-DAY FREE TRIAL'}
        </button>

        <p className="text-[10px] text-white/40 text-center mt-3 leading-relaxed">
          Card required. Free for {terms.trial_days} days, then ${terms.trial_total_after_trial} for 3 months, then ${terms.renews_at}/mo. Cancel anytime in your account.
        </p>
      </div>
    </div>
  );
}

